import type { DriveStep } from 'driver.js';
import { filterExistingSteps } from './tourFilter';
import { withMobileSidebarOpen } from './tourMobileSidebar';

// Full catalog of reference-tour steps across the whole app. Steps whose
// `element` selector isn't present in the current page's DOM are dropped by
// getReferenceSteps() below — this is what makes the reference tour
// "page-aware" without any route-detection logic: it just reflects whatever
// happens to be on screen when the user clicks the Tutorial button.
//
// Steps targeting `#tour-nav-*` live inside the sidebar, which is an
// off-canvas drawer below SIDEBAR_DRAWER_QUERY (hooks/useIsMobile.ts, mirroring
// Layout.scss) — each is wrapped with withMobileSidebarOpen so the drawer opens
// for the step and closes again once the tour moves on, at those widths only.
const ALL_STEPS: DriveStep[] = [
  {
    popover: {
      title: 'Welcome to Reality Coach!',
      description: "Here's a quick reference tour of the tools available to you. Click Next to continue, or Close to explore on your own.",
      showButtons: ['next', 'close'],
    },
  },
  withMobileSidebarOpen({
    element: '#tour-nav-home',
    popover: {
      title: 'Home',
      description: 'Your landing page. It shows what needs your attention right now, tailored to your role.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-roleplay',
    popover: {
      title: 'Role Play',
      description: 'Start a new roleplay session with an AI-powered student avatar.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-feedback',
    popover: {
      title: 'Feedback',
      description: 'Review past sessions, scores, and AI coaching feedback here.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-users',
    popover: {
      title: 'Users',
      description: 'Manage the users in your organization.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-join-requests',
    popover: {
      title: 'Join Requests',
      description: 'Review people asking to join your organization, approve or decline them, and move approved members into a seat.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-dashboard',
    popover: {
      title: 'Dashboard',
      description: 'View analytics and reporting across your organization.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-app-feedback',
    popover: {
      title: 'App Feedback',
      description: 'See feedback that users have submitted about the app.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-organizations',
    popover: {
      title: 'Organizations',
      description: 'Manage organizations and their sub-organizations.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-reports',
    popover: {
      title: 'Reports',
      description: 'Track trial, subscribed, renewal, and abandoned-signup users across the platform.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-characters',
    popover: {
      title: 'Characters',
      description: 'Manage the AI students used in roleplay, including how young each one sounds.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-scene-editor',
    popover: {
      title: 'Scene Editor',
      description: 'Configure the 3D classroom scenes used in roleplay sessions.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-prompt-studio',
    popover: {
      title: 'Prompt Studio',
      description: 'Author and edit the AI prompts that drive roleplay scenarios.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-reviews',
    popover: {
      title: 'Reviews',
      description: 'Manage role play review requests awaiting assignment.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-reviewer-dashboard',
    popover: {
      title: 'My Reviews',
      description: 'Review roleplay sessions assigned to you.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-my-assignments',
    popover: {
      title: 'My Assignments',
      description: 'Track your assigned training bundles and progress.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-assignments',
    popover: {
      title: 'Assignments',
      description: 'Create and manage training assignments for your organization.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-bundles',
    popover: {
      title: 'Bundles',
      description: 'Create and manage content bundles for assignments.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-my-progress',
    popover: {
      title: 'My Progress',
      description: 'Track your XP, streaks, and badges as you practice.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-team-progress',
    popover: {
      title: 'Team Progress',
      description: 'Track XP, streaks, and progress across everyone in your organization.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-platform-dashboard',
    popover: {
      title: 'Platform Overview',
      description: 'High-level stats across the whole platform.',
    },
  }),
  withMobileSidebarOpen({
    element: '#tour-nav-competitions',
    popover: {
      title: 'Competitions',
      description: 'Join or manage competitions and check the leaderboard.',
    },
  }),
  {
    element: '#tour-header-help',
    popover: {
      title: 'Send Feedback',
      description: 'Have a suggestion or found a bug? Send us feedback anytime.',
    },
  },
  {
    element: '#tour-header-bell',
    popover: {
      title: 'Notifications',
      description: 'Check here for updates and alerts relevant to you.',
    },
  },
  {
    element: '#tour-header-user-menu',
    popover: {
      title: 'User Menu',
      description: 'Access your profile, device setup, and log out from here.',
    },
  },
  {
    element: '#tour-header-tutorial',
    popover: {
      title: 'Tutorial',
      description: 'Click this button anytime to replay this reference tour.',
    },
  },
  {
    element: '#tour-scenario-selector',
    popover: {
      title: 'Scenario Selector',
      description: 'Choose a roleplay scenario to begin a new session.',
    },
  },
  {
    element: '#tour-classroom-scene',
    popover: {
      title: 'Classroom & Avatars',
      description: 'Click on a student avatar in the classroom to start your roleplay session.',
    },
  },
  {
    element: '#tour-mic-button',
    popover: {
      title: 'Push-to-Talk Mic',
      description: 'Click here to mute or unmute your mic during the session.',
    },
  },
  {
    element: '#tour-end-session',
    popover: {
      title: 'End Session',
      description: "Click here whenever you're ready to wrap up the session.",
    },
  },
  {
    element: '#tour-sessions-list',
    popover: {
      title: 'Session History',
      description: 'Browse all of your past roleplay sessions here.',
    },
  },
  {
    element: '#tour-sessions-search',
    popover: {
      title: 'Search Sessions',
      description: 'Search sessions by ID, number, user, or module.',
    },
  },
  {
    element: '#tour-session-kpis',
    popover: {
      title: 'Score & KPIs',
      description: 'See your score, steps completed, duration, and trainer rating for this session.',
    },
  },
  {
    element: '#tour-session-feedback',
    popover: {
      title: 'AI Coaching Feedback',
      description: 'Read AI-generated coaching feedback on your performance.',
    },
  },
  {
    element: '#tour-session-steps',
    popover: {
      title: 'Teaching Steps',
      description: 'Track how well you completed each step of the teaching method.',
    },
  },
  {
    element: '#tour-session-recommendations',
    popover: {
      title: 'Recommendations',
      description: 'Get recommended resources based on your session performance.',
    },
  },
  {
    element: '#tour-session-transcript',
    popover: {
      title: 'Transcript',
      description: 'Review the full conversation transcript from this session.',
    },
  },
  {
    element: '#tour-session-human-review',
    popover: {
      title: 'Human Review',
      description: 'Trainers and admins can also send a session to a person for review. Their rating and comments appear here.',
    },
  },
  {
    popover: {
      title: "That's the tour!",
      description: 'Click the Tutorial button in the header anytime you want to see this again.',
      showButtons: ['next', 'close'],
      doneBtnText: 'Finish',
    },
  },
];

export function getReferenceSteps(): DriveStep[] {
  return filterExistingSteps(ALL_STEPS);
}
